/**
 * BrassLink -- the one in-app link treatment (P124.2 / brand P1.5).
 *
 * Every navigational link looks identical: brass, underline-on-hover.
 * Internal routes pass `to` (react-router Link); external URLs pass
 * `href` (+ `external` for a new tab with the safe rel). This retires
 * the ad-hoc `text-*-700 hover:underline` links scattered across modules.
 *
 * NOT for destructive actions (use <Button variant="danger">) or primary
 * CTAs (use <Button variant="primary">).
 */
import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { Link } from "react-router-dom";

const BRASS = "font-medium text-brand-brassDark hover:underline";

interface CommonProps {
  testid?: string;
  className?: string;
  children: ReactNode;
}

type InternalProps = CommonProps & {
  to: string;
  href?: never;
  external?: never;
};

type ExternalProps = CommonProps &
  Omit<ComponentPropsWithoutRef<"a">, "href" | "className"> & {
    href: string;
    to?: never;
    /** Open in a new tab with rel="noopener noreferrer". */
    external?: boolean;
  };

export function BrassLink(props: InternalProps | ExternalProps) {
  const cls = [BRASS, props.className ?? ""].filter(Boolean).join(" ");

  if ("to" in props && props.to !== undefined) {
    const { to, testid, children } = props;
    return (
      <Link to={to} data-testid={testid} className={cls}>
        {children}
      </Link>
    );
  }

  const { href, external, testid, children, className: _c, ...rest } = props;
  return (
    <a
      href={href}
      data-testid={testid}
      className={cls}
      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      {...rest}
    >
      {children}
    </a>
  );
}
